/**
 * Prescription required-field validators (shared by the new-order wizard
 * and the Edit prescription modal).
 *
 * These pure functions take the current PrescriptionFormState plus the
 * selected SolutionSubtype and return either:
 *   - a list of MissingField records (human-readable label + machine key),
 *     or
 *   - the first error message (string | null) for legacy single-issue
 *     gating (Next button reason text).
 *
 * The rules mirror the wizard's original behaviour exactly. When you
 * change a rule here, both the wizard "Next" gate and the Edit dialog
 * "Update prescription" gate move together.
 */

// Import the type from the defaults sibling, NOT PrescriptionForm, so this
// module (and its unit tests) stays free of the eager @/lib/supabase import.
import type { PrescriptionFormState } from './prescriptionFormDefaults';
import type { SolutionSubtype } from './SolutionSubtypePicker';

export interface MissingField {
  /** Stable machine key (useful for tests / analytics). */
  key: string;
  /** Human-readable label shown in the "Required: ..." notice. */
  label: string;
}

/** R36 — Sandal-design required-field gate (orthopaedic_sandal only).
 *  All eight sandal-configurator fields are required: model picker, three
 *  colour selects, three feature dropdowns, and the custom name text. */
export function collectSandalDesignMissing(
  form: PrescriptionFormState,
  subtype: SolutionSubtype | null,
): MissingField[] {
  const out: MissingField[] = [];
  if (subtype !== 'orthopaedic_sandal') return out;
  const s = form.deep?.sandal;
  if (!s) {
    out.push({
      key: 'sandal',
      label: 'Sandal configuration (model, colors, features, custom name)',
    });
    return out;
  }
  if (!s.model) out.push({ key: 'sandal_model', label: 'Sandal model' });
  if (!s.base_color) out.push({ key: 'sandal_base_color', label: 'Base color' });
  if (!s.upper_strap_color) {
    out.push({ key: 'sandal_upper_strap_color', label: 'Upper strap color' });
  }
  if (!s.top_cover) out.push({ key: 'sandal_top_cover', label: 'Top cover' });
  if (!s.white_midsole) out.push({ key: 'sandal_white_midsole', label: 'White midsole' });
  if (!s.posterior_strap) {
    out.push({ key: 'sandal_posterior_strap', label: 'Posterior strap' });
  }
  if (!s.upper_strap_size) {
    out.push({ key: 'sandal_upper_strap_size', label: 'Upper strap size' });
  }
  // custom_name is OPTIONAL (matches InsoleDesignState.custom_name convention
  // — patient name to engrave / leave blank).
  return out;
}

/** Insole-design required-field gate (custom non-sandal subtypes only):
 *  thickness, length, cup, top cover. */
export function collectInsoleDesignMissing(
  form: PrescriptionFormState,
  subtype: SolutionSubtype | null,
): MissingField[] {
  const out: MissingField[] = [];
  if (subtype === 'orthopaedic_sandal') return out;
  const d = form.deep?.design;
  if (!d) {
    // Nothing seeded yet — surface the whole design block as missing
    // rather than emitting four separate items. Refurbishment uses its
    // own (length + top cover only) label so the wizard's missing-field
    // notice matches the trimmed UI.
    if (subtype === 'insoles_refurbishment') {
      out.push({ key: 'design', label: 'Insole refurbishment (length, top cover)' });
    } else {
      out.push({ key: 'design', label: 'Insole design (thickness, length, cup, top cover)' });
    }
    return out;
  }
  // Refurbishment runs through a stripped-down form: only insole length
  // and top cover material are required. No shell thickness (the existing
  // shell is being re-covered), no insole cup, no custom name. Branch is
  // placed BEFORE the per-material checks so refurbishment never falls
  // into the carbon/fibreglass preset-thickness branch by accident.
  if (subtype === 'insoles_refurbishment') {
    if (!d.insole_length) {
      out.push({ key: 'insole_length', label: 'Insole length' });
    }
    if (!d.top_cover_material) {
      out.push({ key: 'top_cover_material', label: 'Top cover' });
    }
    return out;
  }
  const isTextBox =
    subtype === 'pa12_insole' ||
    subtype === 'tpu_insole' ||
    subtype === 'polypropylene_insole';
  if (isTextBox) {
    if (d.shell_thickness_mm == null) {
      out.push({ key: 'shell_thickness_mm', label: 'Shell thickness' });
    }
  } else if (subtype === 'eva_insole') {
    if (!d.eva_type) {
      out.push({ key: 'eva_type', label: 'EVA type' });
    }
  } else {
    // carbon / fibreglass — thickness is a preset string
    if (!d.shell_thickness) {
      out.push({ key: 'shell_thickness', label: 'Shell thickness' });
    }
  }
  if (!d.insole_length) {
    out.push({ key: 'insole_length', label: 'Insole length' });
  }
  if (!d.insole_cup) {
    out.push({ key: 'insole_cup', label: 'Insole cup' });
  }
  if (d.insole_cup === 'other' && !(d.insole_cup_other ?? '').trim()) {
    out.push({ key: 'insole_cup_other', label: 'Custom insole cup description' });
  }
  if (!d.top_cover_material) {
    out.push({ key: 'top_cover_material', label: 'Top cover' });
  }
  return out;
}

/** Required-when-revealed validator for the modification cards. Any
 *  field whose parent select made it visible MUST have a value. */
export function collectRequiredHeightsMissing(
  form: PrescriptionFormState,
): MissingField[] {
  const out: MissingField[] = [];
  const deep = form.deep;
  if (!deep) return out;

  const hr = deep.rearfoot?.heel_raise;
  if (hr) {
    if (hr.left_type && hr.left_type !== 'none' && hr.left_height_mm == null) {
      out.push({ key: 'heel_raise_left_height_mm', label: 'Heel raise height (L)' });
    }
    if (hr.right_type && hr.right_type !== 'none' && hr.right_height_mm == null) {
      out.push({ key: 'heel_raise_right_height_mm', label: 'Heel raise height (R)' });
    }
  }

  const aha = deep.midfoot?.arch_height_adjustment;
  if (aha) {
    if (
      aha.mla_left_level &&
      aha.mla_left_level !== 'none' &&
      aha.mla_left_height_mm == null
    ) {
      out.push({ key: 'arch_mla_left_height_mm', label: 'Arch height (MLA, L)' });
    }
    if (
      aha.mla_right_level &&
      aha.mla_right_level !== 'none' &&
      aha.mla_right_height_mm == null
    ) {
      out.push({ key: 'arch_mla_right_height_mm', label: 'Arch height (MLA, R)' });
    }
    if (
      aha.lla_left_level &&
      aha.lla_left_level !== 'none' &&
      aha.lla_left_height_mm == null
    ) {
      out.push({ key: 'arch_lla_left_height_mm', label: 'Arch height (LLA, L)' });
    }
    if (
      aha.lla_right_level &&
      aha.lla_right_level !== 'none' &&
      aha.lla_right_height_mm == null
    ) {
      out.push({ key: 'arch_lla_right_height_mm', label: 'Arch height (LLA, R)' });
    }
  }

  const fw = deep.forefoot?.forefoot_wedge;
  if (fw) {
    if (fw.left_type && fw.left_type !== 'none' && fw.left_height_mm == null) {
      out.push({ key: 'forefoot_wedge_left_height_mm', label: 'Forefoot wedge height (L)' });
    }
    if (fw.right_type && fw.right_type !== 'none' && fw.right_height_mm == null) {
      out.push({ key: 'forefoot_wedge_right_height_mm', label: 'Forefoot wedge height (R)' });
    }
  }

  return out;
}

/** R34 - Offloading dependents. When the first dropdown is set to a
 *  real value, the second dropdown (softness / thickness) becomes
 *  required. */
export function collectRequiredOffloadingFollowUpMissing(
  form: PrescriptionFormState,
): MissingField[] {
  const out: MissingField[] = [];
  const o = form.deep?.offloadings;
  if (!o) return out;

  const mco = o.metatarsal_cut_outs;
  if (mco) {
    if (
      mco.left_location &&
      mco.left_location !== 'none' &&
      (!mco.left_softness || !mco.left_thickness)
    ) {
      out.push({
        key: 'metatarsal_cut_out_left',
        label: 'Metatarsal cut-out softness + thickness (L)',
      });
    }
    if (
      mco.right_location &&
      mco.right_location !== 'none' &&
      (!mco.right_softness || !mco.right_thickness)
    ) {
      out.push({
        key: 'metatarsal_cut_out_right',
        label: 'Metatarsal cut-out softness + thickness (R)',
      });
    }
  }

  const kw = o.kinetic_wedge;
  if (kw) {
    const lt = kw.left_thickness ?? kw.left_height;
    const rt = kw.right_thickness ?? kw.right_height;
    if (kw.left_softness && !lt) {
      out.push({ key: 'kinetic_wedge_left_thickness', label: 'Kinetic wedge thickness (L)' });
    }
    if (kw.right_softness && !rt) {
      out.push({ key: 'kinetic_wedge_right_thickness', label: 'Kinetic wedge thickness (R)' });
    }
  }

  const dmp = o.drop_met_pad;
  if (dmp) {
    if (dmp.left_location && dmp.left_location !== 'none' && !dmp.left_thickness) {
      out.push({ key: 'drop_met_pad_left_thickness', label: 'Drop met pad thickness (L)' });
    }
    if (dmp.right_location && dmp.right_location !== 'none' && !dmp.right_thickness) {
      out.push({ key: 'drop_met_pad_right_thickness', label: 'Drop met pad thickness (R)' });
    }
  }
  // R34 - MTT bar, Subcapital bar, Extra heel cushion all auto-seed
  // their thickness on location pick, so they never need a follow-up.
  return out;
}

/**
 * Aggregate all missing-required fields for a prescription. Order is
 * stable: insole design first, then required heights, then offloading
 * follow-ups — same priority the wizard's `canProceedFromStepWithReason`
 * applies when picking which message to display.
 */
export function collectPrescriptionMissingFields(
  form: PrescriptionFormState,
  subtype: SolutionSubtype | null,
): MissingField[] {
  return [
    ...collectSandalDesignMissing(form, subtype),
    ...collectInsoleDesignMissing(form, subtype),
    ...collectRequiredHeightsMissing(form),
    ...collectRequiredOffloadingFollowUpMissing(form),
  ];
}

/* -------------------------------------------------------------------- */
/*  Single-message helpers (kept for the wizard's existing Next-button  */
/*  reason text — returns the FIRST issue only, matching legacy copy).  */
/* -------------------------------------------------------------------- */

/** R36 — first-issue gate for the sandal configurator. Returns null for
 *  non-sandal subtypes so the existing custom-insole flow is untouched. */
export function checkSandalDesign(
  form: PrescriptionFormState,
  subtype: SolutionSubtype | null,
): string | null {
  if (subtype !== 'orthopaedic_sandal') return null;
  const s = form.deep?.sandal;
  if (!s) return 'Configure the sandal (model, colors, features, custom name).';
  if (!s.model) return 'Pick a sandal model.';
  if (!s.base_color) return 'Select the base color.';
  if (!s.upper_strap_color) return 'Select the upper strap color.';
  if (!s.top_cover) return 'Select the top cover.';
  if (!s.white_midsole) return 'Select whether the sandal has a white midsole.';
  if (!s.posterior_strap) return 'Select whether the sandal has a posterior strap.';
  if (!s.upper_strap_size) return 'Select the upper strap size.';
  // custom_name is OPTIONAL — see collectSandalDesignMissing.
  return null;
}

export function checkInsoleDesign(
  form: PrescriptionFormState,
  subtype: SolutionSubtype | null,
): string | null {
  if (subtype === 'orthopaedic_sandal') return null;
  const d = form.deep?.design;
  if (!d) {
    return subtype === 'insoles_refurbishment'
      ? 'Complete the refurbishment (length, top cover).'
      : 'Complete the insole design (thickness, length, cup, top cover).';
  }
  // Refurbishment short-circuit (mirrors collectInsoleDesignMissing) —
  // only length + top cover material are gated for refurbishment jobs.
  if (subtype === 'insoles_refurbishment') {
    if (!d.insole_length) return 'Select the insole length.';
    if (!d.top_cover_material) return 'Select the top cover.';
    return null;
  }
  const isTextBox =
    subtype === 'pa12_insole' ||
    subtype === 'tpu_insole' ||
    subtype === 'polypropylene_insole';
  if (isTextBox) {
    if (d.shell_thickness_mm == null) return 'Set the shell thickness.';
  } else if (subtype === 'eva_insole') {
    if (!d.eva_type) return 'Select an EVA type.';
  } else {
    if (!d.shell_thickness) return 'Select the shell thickness.';
  }
  if (!d.insole_length) return 'Select the insole length.';
  if (!d.insole_cup) return 'Select the insole cup.';
  if (d.insole_cup === 'other' && !(d.insole_cup_other ?? '').trim()) {
    return 'Specify the custom insole cup.';
  }
  if (!d.top_cover_material) return 'Select the top cover.';
  return null;
}

export function checkRequiredHeights(form: PrescriptionFormState): string | null {
  const deep = form.deep;
  if (!deep) return null;
  const hr = deep.rearfoot?.heel_raise;
  if (hr) {
    if (hr.left_type && hr.left_type !== 'none' && hr.left_height_mm == null) {
      return 'Set the Heel raise height for L.';
    }
    if (hr.right_type && hr.right_type !== 'none' && hr.right_height_mm == null) {
      return 'Set the Heel raise height for R.';
    }
  }
  const aha = deep.midfoot?.arch_height_adjustment;
  if (aha) {
    if (
      aha.mla_left_level &&
      aha.mla_left_level !== 'none' &&
      aha.mla_left_height_mm == null
    ) {
      return 'Set the Arch height (MLA, L).';
    }
    if (
      aha.mla_right_level &&
      aha.mla_right_level !== 'none' &&
      aha.mla_right_height_mm == null
    ) {
      return 'Set the Arch height (MLA, R).';
    }
    if (
      aha.lla_left_level &&
      aha.lla_left_level !== 'none' &&
      aha.lla_left_height_mm == null
    ) {
      return 'Set the Arch height (LLA, L).';
    }
    if (
      aha.lla_right_level &&
      aha.lla_right_level !== 'none' &&
      aha.lla_right_height_mm == null
    ) {
      return 'Set the Arch height (LLA, R).';
    }
  }
  const fw = deep.forefoot?.forefoot_wedge;
  if (fw) {
    if (fw.left_type && fw.left_type !== 'none' && fw.left_height_mm == null) {
      return 'Set the Forefoot wedge height for L.';
    }
    if (fw.right_type && fw.right_type !== 'none' && fw.right_height_mm == null) {
      return 'Set the Forefoot wedge height for R.';
    }
  }
  return null;
}

export function checkRequiredOffloadingFollowUp(
  form: PrescriptionFormState,
): string | null {
  const o = form.deep?.offloadings;
  if (!o) return null;
  const mco = o.metatarsal_cut_outs;
  if (mco) {
    if (
      mco.left_location &&
      mco.left_location !== 'none' &&
      (!mco.left_softness || !mco.left_thickness)
    ) {
      return 'Complete the Metatarsal cut-out (Softness + Thickness) for L.';
    }
    if (
      mco.right_location &&
      mco.right_location !== 'none' &&
      (!mco.right_softness || !mco.right_thickness)
    ) {
      return 'Complete the Metatarsal cut-out (Softness + Thickness) for R.';
    }
  }
  const kw = o.kinetic_wedge;
  if (kw) {
    const lt = kw.left_thickness ?? kw.left_height;
    const rt = kw.right_thickness ?? kw.right_height;
    if (kw.left_softness && !lt) return 'Set the Kinetic wedge thickness for L.';
    if (kw.right_softness && !rt) return 'Set the Kinetic wedge thickness for R.';
  }
  const dmp = o.drop_met_pad;
  if (dmp) {
    if (dmp.left_location && dmp.left_location !== 'none' && !dmp.left_thickness) {
      return 'Set the Drop met pad thickness for L.';
    }
    if (dmp.right_location && dmp.right_location !== 'none' && !dmp.right_thickness) {
      return 'Set the Drop met pad thickness for R.';
    }
  }
  return null;
}
